import { ApiError, type RequestInfo } from "@circle-react/config/CustomErrors";
import { csrfTokenHeader } from "@circle-react/helpers/csrfToken";

import { t } from "@/i18n-js/instance";

import { safeJsonParse } from "./safeJsonParse";

export interface ErrorJsonBody {
  error_details?: string | Record<string, unknown>;
  failure?: {
    message: string;
  };
  message?: string;
  view_only_masquerading?: boolean;
}

export interface JsonFetchOptions extends RequestInit {
  headers?: Record<string, string>;
  observeResponseHeader?: { name: string; onValue(value: string | null): void };
}

const getAnalyticsTrackerToken = (): string | undefined =>
  window.ANALYTICS_TRACKER_API_TOKEN;

const CSRF_PROTECTED_METHODS = new Set(["DELETE", "PATCH", "POST", "PUT"]);

const hasHeader = (headers: Record<string, string>, header: string): boolean =>
  new Headers(headers).has(header);

const isSameOrigin = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    // If URL parsing fails, assume same-origin to avoid blocking the request
    return true;
  }
};

const withFrontendVersionHeader = ({
  headers,
  url,
}: {
  headers: Record<string, string>;
  url: string;
}): Record<string, string> => {
  if (!isSameOrigin(url) || hasHeader(headers, "X-Circle-Frontend-Version")) {
    return headers;
  }

  return {
    ...headers,
    "X-Circle-Frontend-Version": window.CIRCLE_APP_VERSION || "unknown",
  };
};

const withCsrfTokenHeader = ({
  headers,
  method,
  url,
}: {
  headers: Record<string, string>;
  method: string;
  url: string;
}): Record<string, string> => {
  if (
    !CSRF_PROTECTED_METHODS.has(method.toUpperCase()) ||
    !isSameOrigin(url) ||
    hasHeader(headers, "X-CSRF-Token")
  ) {
    return headers;
  }

  return { ...headers, ...csrfTokenHeader() };
};

export async function jsonFetchRequest<ResponseData>(
  url: string,
  options?: JsonFetchOptions,
) {
  const { observeResponseHeader, ...requestOptions } = options ?? {};
  const method = requestOptions.method || "GET";
  let headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...requestOptions.headers,
  };

  // Keep analytics authorization same-origin and preserve caller-set headers.
  const token = getAnalyticsTrackerToken();
  if (token && isSameOrigin(url) && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  headers = withFrontendVersionHeader({ headers, url });
  headers = withCsrfTokenHeader({ headers, method, url });

  const requestInfo = { url, method, headers, body: requestOptions.body };

  const response = await fetch(url, {
    ...requestOptions,
    headers,
    credentials: "same-origin",
  });

  if (response.ok) {
    if (isSameOrigin(url) && observeResponseHeader) {
      observeResponseHeader.onValue(
        response.headers.get(observeResponseHeader.name),
      );
    }

    try {
      const text = await response.text();
      if (text.length === 0) return undefined as ResponseData;
      return JSON.parse(text) as ResponseData;
    } catch (error: unknown) {
      const details = error instanceof Error ? error.message : "";
      throw new ApiError({
        message: `Failed to parse response`,
        status: response.status,
        statusText: response.statusText,
        errorDetails: details,
        request: requestInfo,
      });
    }
  } else {
    return await throwApiError(response, requestInfo);
  }
}

const throwApiError = async (response: Response, request?: RequestInfo) => {
  let json: ErrorJsonBody;

  try {
    const text = await response.text();
    json = text.length > 0 ? (JSON.parse(text) as ErrorJsonBody) : {};
  } catch {
    json = { message: `Failed to parse error response` };
  }

  const { status, statusText } = response;

  if (json?.view_only_masquerading) {
    throw new ApiError({
      message: t("view_only_error"),
      status,
      statusText,
      errorDetails: t("view_only_error"),
      body: json,
      request,
      response,
    });
  }

  const message =
    json?.message || json?.failure?.message || "Failed to retrieve data!";

  const { error_details = json?.message } = json;

  throw new ApiError({
    message,
    status,
    statusText,
    errorDetails: error_details,
    body: json,
    request,
    response,
  });
};

export const reactQueryGet = <ResponseData = any>(
  url: string,
  options?: Pick<JsonFetchOptions, "signal">,
) => jsonFetchRequest<ResponseData>(url, { method: "GET", ...options });

export const reactQueryPut = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PUT",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPatch = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PATCH",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPost = <
  ResponseData = any,
  BodyParameters extends object = object,
>(
  url: string,
  bodyParams?: BodyParameters,
  options?: Pick<JsonFetchOptions, "observeResponseHeader" | "signal">,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "POST",
    body: JSON.stringify(bodyParams),
    ...options,
  });

export const reactQueryDelete = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) => {
  const options: JsonFetchOptions = {
    method: "DELETE",
  };

  if (bodyParams) {
    options.body = JSON.stringify(bodyParams);
  }

  return jsonFetchRequest<ResponseData>(url, options);
};

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export interface FetchOptions extends Partial<RequestInit> {
  forReactQuery?: boolean;
  headers?: Record<string, string>;
}

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export type OptionsType = FetchOptions | undefined;

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export const serializeJsonResponse = async <ResponseData>(
  response: Response,
): Promise<ResponseData | undefined | Record<string, never>> => {
  if (response.ok) {
    const text = await response.text();
    if (text.length > 0) {
      const parsed = safeJsonParse<ResponseData>(text);
      return parsed || {};
    }
    return {};
  }

  return throwApiError(response);
};

/**
 * @deprecated Please use jsonFetchRequest from backendRequestHelpers.ts instead, or write your own fetch() call
 */
export async function fetchRequest<ResponseData>(
  url: string,
  options: OptionsType = {},
): Promise<Response | ResponseData | Record<string, never> | undefined> {
  const {
    headers = {},
    forReactQuery: isForReactQuery,
    ...passThroughOptions
  } = options;

  let mergedHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
  };

  // Add JWT token for request enrichment (rate limiting, analytics, etc.)
  // Only attach to same-origin requests to prevent token leakage to third-party services.
  const token = getAnalyticsTrackerToken();
  if (token && isSameOrigin(url) && !mergedHeaders.Authorization) {
    mergedHeaders.Authorization = `Bearer ${token}`;
  }

  mergedHeaders = withFrontendVersionHeader({ headers: mergedHeaders, url });
  mergedHeaders = withCsrfTokenHeader({
    headers: mergedHeaders,
    method: passThroughOptions.method || "GET",
    url,
  });

  const response = await fetch(url, {
    ...passThroughOptions,
    headers: mergedHeaders,
    credentials: "same-origin",
  });

  if (!isForReactQuery) {
    return response;
  }

  return serializeJsonResponse<ResponseData>(response);
}
